import { isValidElement, ReactNode, useState } from "react";

import Accordion from "react-bootstrap/Accordion";
import { useAccordionButton } from "react-bootstrap";
import { ApolloError } from "@apollo/client";
import InfiniteScroll from "react-infinite-scroll-component";
import { useInView, InView } from "react-intersection-observer";
import { formatRelative } from "date-fns";

import Skeleton from "../Skeleton";
import LoadingSpinner from "../LoadingSpinner";
import Alert from "../Alert";

import type { Property } from "csstype";

import "./infiniteScrollingList.css";

type PageInfo = {
  endCursor: string | null
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor: string | null
}

type InfiniteScrollingListProps = {
  initialLoading: boolean;
  fetchMoreLoading: boolean;
  refetchLoading?: boolean;
  error: ApolloError | undefined;
  data: Array<Record<string, ReactNode | object>> | undefined;
  pageInfo: PageInfo | undefined;
  pageSize: number;
  listItemWidths?: Property.GridTemplateColumns<string | number> | undefined
  listItemHeight?: number;
  listHeight?: number;
  disableAccordion?: boolean;
  keysToHideInAccordionHeader?: string[];
  windowWidth?: number;
  fetchMoreData: (pageInfo: PageInfo) => void;
  refetchData?: (totalCount: number) => void;
}

export default function InfiniteScrollingList({
  initialLoading,
  fetchMoreLoading,
  refetchLoading,
  error,
  data,
  pageInfo,
  listItemWidths = "1fr 1fr 1fr",
  listItemHeight = 80,
  listHeight = 550,
  disableAccordion = false,
  keysToHideInAccordionHeader = [],
  windowWidth,
  fetchMoreData,
  refetchData
}: InfiniteScrollingListProps) {

  const AccordionToggle = ({ children, rowIndex }: {children: React.ReactNode, rowIndex: number}) => {
    const decoratedOnClick = useAccordionButton(String(rowIndex));

    return (
      <div
        key={rowIndex}
        data-index={rowIndex}
        className={
          rowIndex % 2 ? "list-item-odd" : "list-item-even"
        }
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: listItemWidths,
          padding: "0px 20px",
          minHeight: `${listItemHeight}px`,
          cursor: disableAccordion ? "default" : "pointer",
        }}
        onClick={disableAccordion ? undefined : decoratedOnClick}
      >
        {children}
        {!disableAccordion && <div style={{position: "absolute", right: 0}}><Accordion.Button style={{backgroundColor: "transparent"}} /></div> }
      </div>
    );
  };

  const [ showRefreshAfterInitialScrollUp, setShowRefreshAfterInitialScrollUp ] = useState(false);
  const [ disableRefreshUntilNextScrollUp, setDisableRefreshUntilNextScrollUp ] = useState(false);
  const [ refetchDelay, setRefetchDelay ] = useState(false);
  const [ lastFetchedTimestamp, setLastFetchedTimestamp ] = useState<Date | null>(null);

  const { ref: refreshRef } = useInView({
    threshold: [0.1, 1],
    initialInView: true,
    onChange(inView, entry) {
      if (inView) {
        if (entry.intersectionRatio >= 0.9 && !disableRefreshUntilNextScrollUp) {
          if (refetchData != null) {
            setRefetchDelay(true);
            setTimeout(() => {
              refetchData(data?.length ?? 0);
              setLastFetchedTimestamp(new Date());
              setRefetchDelay(false);
              setDisableRefreshUntilNextScrollUp(true);
            }, 1000);
          }
        }
      } else {
        if (entry.intersectionRatio <= 0.1) {
          if (!showRefreshAfterInitialScrollUp) {
            setShowRefreshAfterInitialScrollUp(true);
          } 
          if (disableRefreshUntilNextScrollUp) {
            setDisableRefreshUntilNextScrollUp(false);
          }
        }
      }
    },
  });

  return (
    <div>
      <div
        id="scrollableDiv"
        data-testid="scroll-container"
        style={{
          height: error == null ? listHeight : "auto",
          width: "100%",
          overflowY: "scroll",
          display: "flex",
          justifyContent: initialLoading ? "start" : "end",
          flexDirection: "column-reverse",
        }}
      >
        { error == null
          ? !initialLoading
            ? <Accordion
              alwaysOpen
              style={{
                width: "100%",
              }}
            >
              <InfiniteScroll
                dataLength={data?.length ?? 0}
                next={() => { 
                  fetchMoreData(pageInfo!); 
                }}
                style={{ display: "flex", flexDirection: "column-reverse", overflow: "visible" }} // To put endMessage and loader to the top.
                inverse={true}
                hasMore={true}
                loader={pageInfo?.hasNextPage === true
                  ? <div
                    className="loading-list-item d-flex align-items-center"
                    style={{
                      padding: "0px 20px",
                      width: "100%",
                      height: "40px",
                    }}
                  >
                    <span><LoadingSpinner /> Loading More...</span>
                  </div>
                  : "Nothing more to load"}
                scrollableTarget="scrollableDiv"
              >
                { !initialLoading && refetchData != null && refetchLoading != null
                  && <div
                    className={`${showRefreshAfterInitialScrollUp ? "refresh-list-item" : "refresh-list-item-empty"} d-flex align-items-center`}
                    ref={refreshRef}
                    style={{
                      padding: "0px 20px",
                      width: "100%",
                      height: "40px",
                    }}
                  >
                    { showRefreshAfterInitialScrollUp
                      ? refetchLoading || refetchDelay
                        ? <span><LoadingSpinner /> Loading More...</span>
                        : `Last refreshed ${formatRelative(lastFetchedTimestamp ?? new Date(), new Date())}`
                      : null
                    }
                  </div>
                }
                {data?.map((row, index) => {
                  if (row != null) return (
                    // eslint-disable-next-line react/no-array-index-key
                    <InView key={`${Object.values(row)[0]}-${index})`}>
                      {({ inView, ref }) => (
                        <Accordion.Item as="div" eventKey={String(index)} ref={ref} style={!inView ? {minHeight: `${listItemHeight}px`} : {}} data-testid={`row-inview-${index}`}>
                          { inView
                            && <>
                              <AccordionToggle rowIndex={index}>
                                {Object.entries(row)?.filter(([key, _]) => !keysToHideInAccordionHeader.includes(key))?.map(([key, value]) => (
                                  <div 
                                    style={{
                                      // For overflowing text (not React components, except <Link />), show ellipsis
                                      overflow: isValidElement(value) && !Object.keys(value.props).includes("to") ? "inherit" : "hidden", 
                                      whiteSpace: "nowrap", 
                                      textOverflow: "ellipsis",
                                      paddingRight: "10px",
                                    }} 
                                    key={key}
                                  >
                                    {typeof value === "object" && !isValidElement(value)
                                      ? Object.values(value ?? {}).map((value: object | string | ReactNode, index) => (
                                        <span 
                                          key={
                                            value == null || value === "null"
                                              ? `ListItemAccordionHeader${index}`
                                              : typeof (value) === "object" 
                                                ? JSON.stringify(value as object, null, 2) 
                                                : value.toString()
                                          }
                                        >
                                          {index !== 0 ? " - " : ""}
                                          {typeof (value) === "object" && !isValidElement(value)
                                            ? JSON.stringify(value as object, null, 2) 
                                            : value
                                          }
                                        </span> 
                                      ))
                                      : value
                                    }
                                  </div>
                                ))}
                              </AccordionToggle>
                              { data != null && !disableAccordion
                                && <Accordion.Body>
                                  {Object.entries(row ?? {}).map(([key, value]) => (
                                    <div key={key} className="pb-1">
                                      <b>{key[0].toUpperCase() + key.slice(1)}</b>: 
                                      {typeof (value) === "object" && !isValidElement(value)
                                        ? Object.entries(value ?? {}).map(([key, value]) => (
                                          <div key={`${key}-${value}`} className="ms-4">
                                            <b>{key[0].toUpperCase() + key.slice(1)}</b>: {JSON.stringify(value, null, 2)}
                                          </div>
                                        ))
                                        : <span>&nbsp;{value}</span>
                                      }
                                    </div>
                                  ))}
                                </Accordion.Body>
                              }
                            </>

                          }
                        </Accordion.Item>
                      )}
                    </InView>
                  );
                })}
              </InfiniteScroll>
            </Accordion>
            : <Skeleton lines={(windowWidth ?? 0) > 3000 ? 12 : 8} height={`${listItemHeight}px`} outerClassName="p-1" />        
          : <Alert variant="danger" className="mx-3" message="There was an error getting list data." />
        }
        { error == null && !initialLoading
          && <>
            <div 
              className="sticky-header"
              style={{
                display: "grid",
                gridTemplateColumns: listItemWidths,
              }}
            >
              {Object.keys(data?.[0] ?? {})?.filter(key => !keysToHideInAccordionHeader.includes(key))?.map((key) => (
                <div key={key}>
                  {key[0].toUpperCase() + key.slice(1)}
                </div>
              ))}
            </div>
          </>
        }
      </div>
    </div>
  );
}